import { useState, useEffect, useCallback } from "react";

/**
 * Loads one record by id.
 *
 * useUser, useProgram, useExercise, useRecipe and usePost were five
 * identical 49-line copies of this - a diff between any two was nothing but
 * identifier renames. useFinancial held two more near-copies.
 *
 * Passing a null id clears the state and skips the fetch, which is what the
 * detail pages need while a route param is still unparsed.
 */
export const useEntity = <T>(
  id: number | null,
  fetcher: (id: number) => Promise<T | null>,
  {
    notFoundMessage,
    errorMessage,
  }: {
    notFoundMessage: string;
    errorMessage: string;
  }
) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetcher(id);

      if (result) {
        setData(result);
      } else {
        setError(notFoundMessage);
        setData(null);
      }
    } catch (err) {
      setError(errorMessage);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, fetcher, notFoundMessage, errorMessage]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    data,
    loading,
    error,
    refresh: load,
    // Lets a detail page patch the record it already holds (e.g. after an
    // inline edit) without a round trip.
    setData,
  };
};
